/* =============================================================
   IELTS Preparation Platform
   Sections: Placement Quiz → Listening → Reading → Writing → Speaking
   ============================================================= */
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle, XCircle, Play, Pause, RotateCcw,
  BookOpen, Headphones, PenLine, Mic, ChevronRight, Clock,
  Trophy, Star, BarChart3, Volume2, AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { nanoid } from "nanoid";

/* ── Session ID (persisted in sessionStorage) ─────────────────── */
function getSessionId() {
  let id = sessionStorage.getItem("ielts_session");
  if (!id) { id = nanoid(); sessionStorage.setItem("ielts_session", id); }
  return id;
}

/* ── Band score color ─────────────────────────────────────────── */
function bandColor(band: string) {
  const n = parseFloat(band);
  if (n >= 7.5) return "#00A86B";
  if (n >= 6.0) return "#1E6FD9";
  if (n >= 5.0) return "#F59E0B";
  return "#EF4444";
}

/* ── CEFR level badge ─────────────────────────────────────────── */
function LevelBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    A1: "#6B7280", A2: "#9CA3AF", B1: "#3B82F6", B2: "#1E6FD9", C1: "#00A86B", C2: "#F59E0B",
  };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white"
      style={{ background: colors[level] ?? "#6B7280" }}>{level}</span>
  );
}

/* ── Audio Player Component ───────────────────────────────────── */
function AudioPlayer({ src, onEnded }: { src: string; onEnded?: () => void }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3">
      <audio ref={audioRef} src={src}
        onTimeUpdate={() => {
          if (!audioRef.current) return;
          setCurrentTime(audioRef.current.currentTime);
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }}
        onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
      />
      <div className="flex items-center gap-4">
        <button onClick={toggle}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: "linear-gradient(135deg,#00A86B,#00C97E)", boxShadow: "0 4px 16px rgba(0,168,107,0.4)" }}>
          {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-4 h-4 text-white/40" />
            <span className="text-white/60 text-xs">{fmt(currentTime)} / {fmt(duration)}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer"
            onClick={(e) => {
              if (!audioRef.current) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = (e.clientX - rect.left) / rect.width;
              audioRef.current.currentTime = pct * audioRef.current.duration;
            }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#00A86B,#1E6FD9)" }} />
          </div>
        </div>
        <button onClick={() => { if (audioRef.current) { audioRef.current.currentTime = 0; setPlaying(false); } }}
          className="text-white/40 hover:text-white transition-colors">
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ── Timer Component ──────────────────────────────────────────── */
function Timer({ seconds, onExpire }: { seconds: number; onExpire: () => void }) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    const t = setInterval(() => setRemaining((r) => {
      if (r <= 1) { clearInterval(t); onExpire(); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [onExpire]);
  const pct = (remaining / seconds) * 100;
  const color = remaining < 60 ? "#EF4444" : remaining < 120 ? "#F59E0B" : "#00A86B";
  return (
    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2">
      <Clock className="w-4 h-4" style={{ color }} />
      <span className="font-mono font-bold text-sm" style={{ color }}>
        {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
      </span>
    </div>
  );
}

/* ── Question Component ───────────────────────────────────────── */
function Question({
  q, index, answer, onChange, showResult,
}: {
  q: { id: string; type: string; text: string; options?: string[] };
  index: number;
  answer: string;
  onChange: (v: string) => void;
  showResult?: { correctAnswer: string; isCorrect: boolean } | null;
}) {
  return (
    <div className={`bg-white/5 border rounded-xl p-5 transition-all ${showResult ? (showResult.isCorrect ? "border-green-500/40" : "border-red-500/40") : "border-white/10"}`}>
      <div className="flex gap-3 mb-4">
        <span className="w-7 h-7 rounded-full bg-[#1E6FD9]/30 text-[#1E6FD9] text-sm font-bold flex items-center justify-center flex-shrink-0">{index + 1}</span>
        <p className="text-white text-sm leading-relaxed">{q.text}</p>
      </div>
      {q.type === "multiple_choice" && q.options && (
        <div className="grid grid-cols-1 gap-2 ml-10">
          {q.options.map((opt) => {
            const isSelected = answer === opt;
            const isCorrect = showResult?.correctAnswer === opt;
            const isWrong = showResult && isSelected && !showResult.isCorrect;
            return (
              <button key={opt} onClick={() => !showResult && onChange(opt)}
                className={`text-left px-4 py-2.5 rounded-lg text-sm transition-all border ${
                  isCorrect && showResult ? "border-green-500 bg-green-500/20 text-green-300" :
                  isWrong ? "border-red-500 bg-red-500/20 text-red-300" :
                  isSelected ? "border-[#1E6FD9] bg-[#1E6FD9]/20 text-white" :
                  "border-white/10 bg-white/5 text-white/70 hover:border-white/30 hover:bg-white/10"
                }`}>
                {opt}
                {isCorrect && showResult && <CheckCircle className="inline w-4 h-4 ml-2 text-green-400" />}
                {isWrong && <XCircle className="inline w-4 h-4 ml-2 text-red-400" />}
              </button>
            );
          })}
        </div>
      )}
      {(q.type === "fill_blank" || q.type === "true_false_ng") && (
        <div className="ml-10">
          {q.type === "true_false_ng" ? (
            <div className="flex gap-2">
              {["TRUE", "FALSE", "NOT GIVEN"].map((opt) => (
                <button key={opt} onClick={() => !showResult && onChange(opt)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                    showResult?.correctAnswer === opt ? "border-green-500 bg-green-500/20 text-green-300" :
                    showResult && answer === opt && !showResult.isCorrect ? "border-red-500 bg-red-500/20 text-red-300" :
                    answer === opt ? "border-[#1E6FD9] bg-[#1E6FD9]/20 text-white" :
                    "border-white/10 bg-white/5 text-white/60 hover:border-white/30"
                  }`}>{opt}</button>
              ))}
            </div>
          ) : (
            <input type="text" value={answer} onChange={(e) => !showResult && onChange(e.target.value)}
              disabled={!!showResult}
              placeholder="Type your answer..."
              className={`w-full bg-white/10 border rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none ${
                showResult ? (showResult.isCorrect ? "border-green-500" : "border-red-500") : "border-white/20 focus:border-[#1E6FD9]"
              }`} />
          )}
          {showResult && !showResult.isCorrect && (
            <p className="text-green-400 text-xs mt-2">✓ Correct answer: <strong>{showResult.correctAnswer}</strong></p>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
type View = "home" | "placement" | "placement-result" | "listening" | "listening-result" | "reading" | "reading-result" | "writing" | "speaking" | "progress";

export default function IeltsPage() {
  const [, navigate] = useLocation();
  const [view, setView] = useState<View>("home");
  const [sessionId] = useState(getSessionId);

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      <Header />
      <AnimatePresence mode="wait">
        {view === "home" && <HomeView key="home" onNavigate={setView} sessionId={sessionId} />}
        {view === "placement" && <PlacementView key="placement" onDone={() => setView("placement-result")} sessionId={sessionId} />}
        {view === "placement-result" && <PlacementResultView key="pr" sessionId={sessionId} onNavigate={setView} />}
        {view === "listening" && <ListeningView key="listening" sessionId={sessionId} onDone={() => setView("listening-result")} />}
        {view === "listening-result" && <ListeningResultView key="lr" sessionId={sessionId} onBack={() => setView("home")} />}
        {view === "reading" && <ReadingView key="reading" sessionId={sessionId} onDone={() => setView("reading-result")} />}
        {view === "reading-result" && <ReadingResultView key="rr" sessionId={sessionId} onBack={() => setView("home")} />}
        {view === "writing" && <WritingView key="writing" onBack={() => setView("home")} />}
        {view === "speaking" && <SpeakingView key="speaking" onBack={() => setView("home")} />}
        {view === "progress" && <ProgressView key="progress" sessionId={sessionId} onBack={() => setView("home")} />}
      </AnimatePresence>
      <Footer />
    </div>
  );
}

/* ── Home View ────────────────────────────────────────────────── */
function HomeView({ onNavigate, sessionId }: { onNavigate: (v: View) => void; sessionId: string }) {
  const { data: progress } = trpc.ielts.getProgress.useQuery({ sessionId });

  const sections = [
    { id: "listening" as View, icon: Headphones, title: "Listening", mn: "Сонсох", color: "#1E6FD9", desc: "4 section, 40 асуулт — аудио сонсоод хариулна", badge: "MP3 аудиотой" },
    { id: "reading" as View, icon: BookOpen, title: "Reading", mn: "Унших", color: "#00A86B", desc: "Passage уншаад T/F/NG болон MC асуулт", badge: "Academic" },
    { id: "writing" as View, icon: PenLine, title: "Writing", mn: "Бичих", color: "#F59E0B", desc: "Task 1 (graph) + Task 2 (essay) — жишиг хариулттай", badge: "Жишиг хариулт" },
    { id: "speaking" as View, icon: Mic, title: "Speaking", mn: "Ярих", color: "#8B5CF6", desc: "Part 1/2/3 асуулт — жишиг хариулт, зөвлөмж", badge: "Жишиг хариулт" },
  ];

  const placementDone = !!progress?.placement;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20">
      {/* Hero */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-[#1E6FD9]/20 border border-[#1E6FD9]/30 rounded-full px-4 py-1.5 text-[#1E6FD9] text-sm font-semibold mb-6">
          🎓 IELTS Бэлтгэлийн Платформ
        </div>
        <h1 className="text-4xl md:text-6xl font-black mb-4">
          IELTS-д <span className="text-[#00A86B]">Бэлд</span>
        </h1>
        <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8">
          Listening, Reading, Writing, Speaking — дөрвөн хэсгийг бүгдийг нь дадлагажуулж, автомат дүнгээр өөрийн түвшинг мэд.
        </p>
        {/* Placement CTA */}
        <div className={`inline-block rounded-2xl p-6 border ${placementDone ? "border-green-500/30 bg-green-500/10" : "border-[#1E6FD9]/30 bg-[#1E6FD9]/10"}`}>
          {placementDone ? (
            <div className="text-center">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-white font-bold mb-1">Таны түвшин: <LevelBadge level={progress!.placement!.level} /> — Band {progress!.placement!.bandScore}</p>
              <button onClick={() => onNavigate("placement")} className="text-white/50 text-sm hover:text-white underline mt-1">Дахин шалгах</button>
            </div>
          ) : (
            <div className="text-center">
              <Star className="w-8 h-8 text-[#1E6FD9] mx-auto mb-2" />
              <p className="text-white font-bold mb-1">Эхлээд түвшин тогтоолго!</p>
              <p className="text-white/50 text-sm mb-4">20 асуулт — 5 минут</p>
              <button onClick={() => onNavigate("placement")}
                className="px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg,#1E6FD9,#3B82F6)", boxShadow: "0 4px 16px rgba(30,111,217,0.4)" }}>
                Түвшин тогтоох Quiz →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {sections.map((s) => {
          const Icon = s.icon;
          const attempts = progress?.attempts.filter((a) => a.section === s.id.replace("-result", "")) ?? [];
          const best = attempts.length ? Math.max(...attempts.map((a) => parseFloat(a.bandScore))) : null;
          return (
            <motion.button key={s.id} onClick={() => onNavigate(s.id)}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className="text-left bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${s.color}20` }}>
                    <Icon className="w-6 h-6" style={{ color: s.color }} />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">{s.mn}</div>
                    <div className="text-white/40 text-sm">{s.title}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${s.color}20`, color: s.color }}>{s.badge}</span>
                  {best && <span className="text-xs text-white/50">Best: Band {best}</span>}
                </div>
              </div>
              <p className="text-white/50 text-sm mb-4">{s.desc}</p>
              <div className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: s.color }}>
                Эхлэх <ChevronRight className="w-4 h-4" />
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Progress button */}
      {(progress?.attempts.length ?? 0) > 0 && (
        <div className="text-center">
          <button onClick={() => onNavigate("progress")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-white/70 hover:text-white hover:border-white/20 transition-all text-sm">
            <BarChart3 className="w-4 h-4" /> Миний явц харах
          </button>
        </div>
      )}
    </motion.div>
  );
}

/* ── Placement Quiz View ──────────────────────────────────────── */
function PlacementView({ onDone, sessionId }: { onDone: () => void; sessionId: string }) {
  const { data: questions } = trpc.ielts.getPlacementQuestions.useQuery();
  const submit = trpc.ielts.submitPlacement.useMutation({ onSuccess: onDone });
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);

  const q = questions?.[current];
  const total = questions?.length ?? 20;
  const answered = Object.keys(answers).length;

  const handleSubmit = () => {
    submit.mutate({
      sessionId,
      answers: Object.entries(answers).map(([id, answer]) => ({ questionId: Number(id), answer })),
    });
  };

  if (!questions) return <div className="container py-32 text-center text-white/50">Ачааллаж байна...</div>;

  if (!started) return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="container py-32 max-w-2xl mx-auto">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-[#1E6FD9]/20 flex items-center justify-center mx-auto mb-6">
          <Star className="w-10 h-10 text-[#1E6FD9]" />
        </div>
        <h2 className="text-3xl font-black text-white mb-4">Англи хэлний Түвшин Тогтоох</h2>
        <p className="text-white/60 mb-2">English Level Placement Quiz</p>
        <div className="grid grid-cols-3 gap-4 my-8">
          {[["20", "Асуулт"], ["5-10", "Минут"], ["A1→C2", "Түвшин"]].map(([v, l]) => (
            <div key={l} className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-2xl font-black text-[#1E6FD9]">{v}</div>
              <div className="text-white/50 text-sm">{l}</div>
            </div>
          ))}
        </div>
        <button onClick={() => setStarted(true)}
          className="px-8 py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-105"
          style={{ background: "linear-gradient(135deg,#1E6FD9,#3B82F6)", boxShadow: "0 4px 24px rgba(30,111,217,0.4)" }}>
          Эхлэх →
        </button>
      </div>
    </motion.div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 max-w-2xl mx-auto">
      {/* Progress bar */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-white/50 text-sm">{current + 1} / {total}</span>
        <LevelBadge level={q?.level ?? "A1"} />
      </div>
      <div className="h-2 bg-white/10 rounded-full mb-8">
        <div className="h-full rounded-full bg-[#1E6FD9] transition-all" style={{ width: `${((current + 1) / total) * 100}%` }} />
      </div>

      {q && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-6">
          <p className="text-white text-xl font-medium mb-6">{q.question}</p>
          <div className="space-y-3">
            {q.options.map((opt: string) => (
              <button key={opt} onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                className={`w-full text-left px-5 py-3.5 rounded-xl text-sm transition-all border font-medium ${
                  answers[q.id] === opt
                    ? "border-[#1E6FD9] bg-[#1E6FD9]/20 text-white"
                    : "border-white/10 bg-white/5 text-white/70 hover:border-white/30 hover:bg-white/10"
                }`}>{opt}</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}
          className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/50 hover:text-white disabled:opacity-30 transition-all text-sm">
          ← Өмнөх
        </button>
        {current < total - 1 ? (
          <button onClick={() => setCurrent((c) => c + 1)} disabled={!answers[q?.id ?? 0]}
            className="px-6 py-2.5 rounded-xl font-bold text-white transition-all disabled:opacity-30 text-sm"
            style={{ background: "#1E6FD9" }}>
            Дараах →
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={answered < total || submit.isPending}
            className="px-6 py-2.5 rounded-xl font-bold text-white transition-all disabled:opacity-30 text-sm"
            style={{ background: "#00A86B" }}>
            {submit.isPending ? "Илгэж байна..." : `Дуусгах (${answered}/${total})`}
          </button>
        )}
      </div>
    </motion.div>
  );
}

/* ── Placement Result View ────────────────────────────────────── */
function PlacementResultView({ sessionId, onNavigate }: { sessionId: string; onNavigate: (v: View) => void }) {
  const { data } = trpc.ielts.getProgress.useQuery({ sessionId });
  const p = data?.placement;
  if (!p) return null;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="container py-32 max-w-2xl mx-auto text-center">
      <Trophy className="w-20 h-20 mx-auto mb-6" style={{ color: bandColor(p.bandScore) }} />
      <h2 className="text-4xl font-black text-white mb-2">Таны Түвшин</h2>
      <div className="flex items-center justify-center gap-4 mb-8">
        <LevelBadge level={p.level} />
        <span className="text-5xl font-black" style={{ color: bandColor(p.bandScore) }}>Band {p.bandScore}</span>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 text-left">
        <div className="grid grid-cols-2 gap-4">
          <div><div className="text-white/50 text-xs mb-1">Зөв хариулт</div><div className="text-white font-bold text-2xl">{p.score}/20</div></div>
          <div><div className="text-white/50 text-xs mb-1">CEFR Түвшин</div><div className="text-white font-bold text-2xl">{p.level}</div></div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-white/60 text-sm">
            {p.level === "C2" ? "Маш өндөр түвшин! IELTS 9.0 зорилт тавь." :
             p.level === "C1" ? "Дэвшилтэт түвшин. IELTS 7.5+ хүрэх боломжтой." :
             p.level === "B2" ? "Дунд-дээд түвшин. IELTS 6.5-7.0 зорилт тавь." :
             p.level === "B1" ? "Дунд түвшин. IELTS 5.5-6.0 зорилт тавь." :
             p.level === "A2" ? "Суурь түвшин. Listening болон Reading-ийг эхлэ." :
             "Эхлэгч түвшин. Суурь дүрмийг давт."}
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={() => onNavigate("listening")}
          className="px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
          style={{ background: "linear-gradient(135deg,#1E6FD9,#3B82F6)" }}>
          Listening эхлэх →
        </button>
        <button onClick={() => onNavigate("home")}
          className="px-6 py-3 rounded-xl font-medium text-white/70 bg-white/5 border border-white/10 hover:text-white transition-all">
          Нүүр хуудас
        </button>
      </div>
    </motion.div>
  );
}

/* ── Listening View ───────────────────────────────────────────── */
function ListeningView({ sessionId, onDone }: { sessionId: string; onDone: () => void }) {
  const { data: sections } = trpc.ielts.getListeningSections.useQuery();
  const submit = trpc.ielts.submitListening.useMutation({ onSuccess: onDone });
  const [activeSection, setActiveSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [submitted, setSubmitted] = useState<Record<number, boolean>>({});
  const [results, setResults] = useState<Record<number, { score: number; total: number; bandScore: string; results: { questionId: string; correctAnswer: string; isCorrect: boolean }[] }>>({});
  const startTime = useRef(Date.now());

  if (!sections) return <div className="container py-32 text-center text-white/50">Ачааллаж байна...</div>;

  const section = sections[activeSection];
  const sectionAnswers = answers[section.id] ?? {};

  const handleSubmitSection = async () => {
    const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
    const res = await submit.mutateAsync({
      sessionId,
      sectionId: section.id,
      answers: section.questions.map((q: { id: string }) => ({ questionId: q.id, answer: sectionAnswers[q.id] ?? "" })),
      timeTakenSeconds: timeTaken,
    });
    setResults((r) => ({ ...r, [section.id]: res }));
    setSubmitted((s) => ({ ...s, [section.id]: true }));
  };

  const allSectionsSubmitted = sections.every((s: { id: number }) => submitted[s.id]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20">
      <div className="flex items-center gap-4 mb-8">
        <Headphones className="w-8 h-8 text-[#1E6FD9]" />
        <div>
          <h2 className="text-2xl font-black text-white">IELTS Listening</h2>
          <p className="text-white/50 text-sm">4 section — 40 асуулт</p>
        </div>
        {!submitted[section.id] && <div className="ml-auto"><Timer seconds={section.durationSeconds + 120} onExpire={handleSubmitSection} /></div>}
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {sections.map((s: { id: number; title: string }, i: number) => (
          <button key={s.id} onClick={() => setActiveSection(i)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all border ${
              activeSection === i ? "border-[#1E6FD9] bg-[#1E6FD9]/20 text-white" :
              submitted[s.id] ? "border-green-500/40 bg-green-500/10 text-green-400" :
              "border-white/10 bg-white/5 text-white/50 hover:text-white"
            }`}>
            {submitted[s.id] ? "✓ " : ""}{s.title.split("—")[0].trim()}
          </button>
        ))}
      </div>

      {/* Audio */}
      <div className="mb-6">
        <h3 className="text-white font-bold mb-2">{section.title}</h3>
        <p className="text-white/50 text-sm mb-4">{section.description}</p>
        <AudioPlayer src={section.audioUrl} />
        <p className="text-white/40 text-xs mt-2 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Аудиог сонсоод асуултуудад хариулна уу. Аудиог нэг удаа л тоглуулна.
        </p>
      </div>

      {/* Questions */}
      <div className="space-y-4 mb-8">
        {section.questions.map((q: { id: string; type: string; text: string; options?: string[] }, i: number) => (
          <Question key={q.id} q={q} index={i}
            answer={sectionAnswers[q.id] ?? ""}
            onChange={(v) => setAnswers((a) => ({ ...a, [section.id]: { ...a[section.id], [q.id]: v } }))}
            showResult={submitted[section.id] ? (results[section.id]?.results.find((r) => r.questionId === q.id) ?? null) : null}
          />
        ))}
      </div>

      {/* Submit / Next */}
      <div className="flex justify-between items-center">
        {!submitted[section.id] ? (
          <button onClick={handleSubmitSection} disabled={submit.isPending}
            className="px-8 py-3 rounded-xl font-bold text-white transition-all hover:scale-105 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#1E6FD9,#3B82F6)" }}>
            {submit.isPending ? "Илгэж байна..." : "Хариулт илгээх"}
          </button>
        ) : (
          <div className="flex items-center gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2">
              <span className="text-white/50 text-sm">Оноо: </span>
              <span className="text-white font-bold">{results[section.id]?.score}/{results[section.id]?.total}</span>
              <span className="text-white/50 text-sm ml-2">Band </span>
              <span className="font-bold" style={{ color: bandColor(results[section.id]?.bandScore ?? "5") }}>{results[section.id]?.bandScore}</span>
            </div>
            {activeSection < sections.length - 1 ? (
              <button onClick={() => setActiveSection((a) => a + 1)}
                className="px-6 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#1E6FD9" }}>
                Дараагийн Section →
              </button>
            ) : allSectionsSubmitted ? (
              <button onClick={onDone}
                className="px-6 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#00A86B" }}>
                Дүн харах →
              </button>
            ) : null}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ── Listening Result View ────────────────────────────────────── */
function ListeningResultView({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { data } = trpc.ielts.getProgress.useQuery({ sessionId });
  const attempts = data?.attempts.filter((a) => a.section === "listening") ?? [];
  const latest = attempts[0];
  if (!latest) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 max-w-2xl mx-auto text-center">
      <Headphones className="w-16 h-16 mx-auto mb-4 text-[#1E6FD9]" />
      <h2 className="text-3xl font-black text-white mb-2">Listening Дүн</h2>
      <div className="text-5xl font-black mb-2" style={{ color: bandColor(latest.bandScore) }}>Band {latest.bandScore}</div>
      <p className="text-white/50 mb-8">{latest.score}/{latest.totalQuestions} зөв хариулт</p>
      <button onClick={onBack} className="px-6 py-3 rounded-xl font-bold text-white" style={{ background: "#1E6FD9" }}>
        Нүүр хуудас руу буцах
      </button>
    </motion.div>
  );
}

/* ── Reading View ─────────────────────────────────────────────── */
function ReadingView({ sessionId, onDone }: { sessionId: string; onDone: () => void }) {
  const { data: passages } = trpc.ielts.getReadingPassages.useQuery();
  const submit = trpc.ielts.submitReading.useMutation({ onSuccess: onDone });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<{ score: number; total: number; bandScore: string; results: { questionId: string; correctAnswer: string; isCorrect: boolean }[] } | null>(null);
  const [passageExpanded, setPassageExpanded] = useState(true);
  const startTime = useRef(Date.now());

  if (!passages || passages.length === 0) return <div className="container py-32 text-center text-white/50">Ачааллаж байна...</div>;
  const passage = passages[0];

  const handleSubmit = async () => {
    const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
    const res = await submit.mutateAsync({
      sessionId,
      passageId: passage.id,
      answers: passage.questions.map((q: { id: string }) => ({ questionId: q.id, answer: answers[q.id] ?? "" })),
      timeTakenSeconds: timeTaken,
    });
    setResults(res);
    setSubmitted(true);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20">
      <div className="flex items-center gap-4 mb-8">
        <BookOpen className="w-8 h-8 text-[#00A86B]" />
        <div>
          <h2 className="text-2xl font-black text-white">IELTS Reading</h2>
          <p className="text-white/50 text-sm">Academic Passage — 10 асуулт</p>
        </div>
        {!submitted && <div className="ml-auto"><Timer seconds={1200} onExpire={handleSubmit} /></div>}
      </div>

      {/* Passage */}
      <div className="bg-white/5 border border-white/10 rounded-2xl mb-6 overflow-hidden">
        <button onClick={() => setPassageExpanded((e) => !e)}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors">
          <div>
            <div className="text-white font-bold">{passage.title}</div>
            <div className="text-white/40 text-xs mt-0.5">Academic Reading Passage</div>
          </div>
          {passageExpanded ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
        </button>
        {passageExpanded && (
          <div className="px-5 pb-5 border-t border-white/10">
            <div className="prose prose-invert max-w-none mt-4">
              {passage.passage.split("\n\n").map((para: string, i: number) => (
                <p key={i} className="text-white/70 text-sm leading-relaxed mb-4">{para}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-4 mb-8">
        {passage.questions.map((q: { id: string; type: string; text: string; options?: string[] }, i: number) => (
          <Question key={q.id} q={q} index={i}
            answer={answers[q.id] ?? ""}
            onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
            showResult={submitted ? (results?.results.find((r) => r.questionId === q.id) ?? null) : null}
          />
        ))}
      </div>

      {!submitted ? (
        <button onClick={handleSubmit} disabled={submit.isPending}
          className="px-8 py-3 rounded-xl font-bold text-white transition-all hover:scale-105 disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#00A86B,#00C97E)" }}>
          {submit.isPending ? "Илгэж байна..." : "Хариулт илгээх"}
        </button>
      ) : (
        <div className="flex items-center gap-4">
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2">
            <span className="text-white/50 text-sm">Оноо: </span>
            <span className="text-white font-bold">{results?.score}/{results?.total}</span>
            <span className="text-white/50 text-sm ml-2">Band </span>
            <span className="font-bold" style={{ color: bandColor(results?.bandScore ?? "5") }}>{results?.bandScore}</span>
          </div>
          <button onClick={onDone} className="px-6 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#00A86B" }}>
            Дүн харах →
          </button>
        </div>
      )}
    </motion.div>
  );
}

/* ── Reading Result View ──────────────────────────────────────── */
function ReadingResultView({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { data } = trpc.ielts.getProgress.useQuery({ sessionId });
  const latest = data?.attempts.filter((a) => a.section === "reading")[0];
  if (!latest) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 max-w-2xl mx-auto text-center">
      <BookOpen className="w-16 h-16 mx-auto mb-4 text-[#00A86B]" />
      <h2 className="text-3xl font-black text-white mb-2">Reading Дүн</h2>
      <div className="text-5xl font-black mb-2" style={{ color: bandColor(latest.bandScore) }}>Band {latest.bandScore}</div>
      <p className="text-white/50 mb-8">{latest.score}/{latest.totalQuestions} зөв хариулт</p>
      <button onClick={onBack} className="px-6 py-3 rounded-xl font-bold text-white" style={{ background: "#00A86B" }}>
        Нүүр хуудас руу буцах
      </button>
    </motion.div>
  );
}

/* ── Writing View ─────────────────────────────────────────────── */
const WRITING_TASKS = [
  {
    id: "task1",
    title: "Task 1 — Graph / Chart Description",
    prompt: "The bar chart below shows the percentage of households in owned and rented accommodation in England and Wales between 1918 and 2011.\n\nSummarise the information by selecting and reporting the main features, and make comparisons where relevant.\n\nWrite at least 150 words.",
    sampleAnswer: "The bar chart illustrates changes in the proportion of households in England and Wales living in owned versus rented accommodation over approximately a century, from 1918 to 2011.\n\nOverall, there was a dramatic shift from rented to owned accommodation over the period. In 1918, the vast majority of households — around 77% — lived in rented accommodation, while only 23% owned their homes. By 2011, this situation had almost completely reversed.\n\nThe proportion of owner-occupied households rose steadily throughout the twentieth century, reaching approximately 70% by 2001. However, there was a slight decline to around 65% by 2011, suggesting a modest reversal of the long-term trend.\n\nConversely, rented accommodation fell consistently from 77% in 1918 to a low of around 30% in 2001, before rising slightly to approximately 35% in 2011.\n\nIn conclusion, while home ownership became the dominant form of tenure during the twentieth century, recent data suggests a partial return towards renting.",
    tips: ["Нийт чиг хандлагыг эхний параграфт тайлбарла", "Тоо баримтыг тодорхой дурдах", "Харьцуулалт хий (whereas, while, compared to)", "Дор хаяж 150 үг бич"],
  },
  {
    id: "task2",
    title: "Task 2 — Essay",
    prompt: "Some people believe that university education should be free for all students. Others argue that students should pay for their own tuition.\n\nDiscuss both views and give your own opinion.\n\nWrite at least 250 words.",
    sampleAnswer: "The question of whether university education should be funded by the state or by individual students is a matter of considerable debate. While there are compelling arguments on both sides, I believe that a mixed funding model offers the most equitable solution.\n\nProponents of free university education argue that it promotes social equality by ensuring that talented individuals from disadvantaged backgrounds are not excluded from higher education due to financial constraints. Countries such as Germany and Norway have demonstrated that free tertiary education is economically viable and produces highly educated workforces. Furthermore, graduates who do not carry student debt are more likely to take risks, start businesses, and contribute to economic innovation.\n\nOn the other hand, those who favour tuition fees contend that university education primarily benefits the individual rather than society as a whole. Graduates typically earn significantly more over their lifetimes than non-graduates, suggesting that it is fair for them to contribute to the cost of their education. Additionally, tuition fees provide universities with funding to maintain high academic standards and invest in research facilities.\n\nIn my view, a balanced approach is most desirable. A system where students pay fees proportional to their future earnings — such as income-contingent loan schemes — allows universities to remain financially sustainable while protecting students from unmanageable debt. This approach, adopted in countries like Australia and the United Kingdom, represents a reasonable compromise between the two extremes.\n\nIn conclusion, while free university education has clear social benefits, a carefully designed fee system that protects low-income students may ultimately be more sustainable and equitable.",
    tips: ["Хоёр талын байр суурийг тэнцвэртэй тайлбарла", "Өөрийн санал тодорхой илэрхийл", "Жишээ, баримт ашигла", "Дор хаяж 250 үг бич", "Introduction, body (2-3 para), conclusion бүтэц"],
  },
];

function WritingView({ onBack }: { onBack: () => void }) {
  const [activeTask, setActiveTask] = useState(0);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [showSample, setShowSample] = useState<Record<string, boolean>>({});
  const task = WRITING_TASKS[activeTask];
  const wordCount = (texts[task.id] ?? "").trim().split(/\s+/).filter(Boolean).length;
  const minWords = activeTask === 0 ? 150 : 250;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20">
      <div className="flex items-center gap-4 mb-8">
        <PenLine className="w-8 h-8 text-[#F59E0B]" />
        <div>
          <h2 className="text-2xl font-black text-white">IELTS Writing</h2>
          <p className="text-white/50 text-sm">Task 1 + Task 2</p>
        </div>
        <button onClick={onBack} className="ml-auto text-white/40 hover:text-white text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Буцах
        </button>
      </div>

      {/* Task tabs */}
      <div className="flex gap-2 mb-6">
        {WRITING_TASKS.map((t, i) => (
          <button key={t.id} onClick={() => setActiveTask(i)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${activeTask === i ? "border-[#F59E0B] bg-[#F59E0B]/20 text-white" : "border-white/10 bg-white/5 text-white/50 hover:text-white"}`}>
            {t.title.split("—")[0].trim()}
          </button>
        ))}
      </div>

      {/* Prompt */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
        <h3 className="text-[#F59E0B] font-bold mb-3">{task.title}</h3>
        {task.prompt.split("\n\n").map((p, i) => (
          <p key={i} className="text-white/70 text-sm leading-relaxed mb-3">{p}</p>
        ))}
      </div>

      {/* Tips */}
      <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl p-4 mb-6">
        <div className="text-[#F59E0B] font-bold text-sm mb-2">💡 Зөвлөмж</div>
        <ul className="space-y-1">
          {task.tips.map((tip, i) => <li key={i} className="text-white/60 text-xs flex gap-2"><span>•</span>{tip}</li>)}
        </ul>
      </div>

      {/* Text area */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-white/50 text-sm">Таны хариулт / Your answer</label>
          <span className={`text-sm font-medium ${wordCount >= minWords ? "text-green-400" : "text-white/40"}`}>
            {wordCount} / {minWords}+ үг
          </span>
        </div>
        <textarea
          value={texts[task.id] ?? ""}
          onChange={(e) => setTexts((t) => ({ ...t, [task.id]: e.target.value }))}
          rows={14}
          placeholder="Write your answer here..."
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#F59E0B] resize-none leading-relaxed"
        />
      </div>

      {/* Sample answer toggle */}
      <button onClick={() => setShowSample((s) => ({ ...s, [task.id]: !s[task.id] }))}
        className="flex items-center gap-2 text-[#F59E0B] text-sm font-medium hover:underline mb-4">
        {showSample[task.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {showSample[task.id] ? "Жишиг хариулт нуух" : "Жишиг хариулт харах (Band 8)"}
      </button>
      {showSample[task.id] && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5 mb-6">
          <div className="text-green-400 font-bold text-sm mb-3">✓ Жишиг хариулт — Band 8</div>
          {task.sampleAnswer.split("\n\n").map((p, i) => (
            <p key={i} className="text-white/70 text-sm leading-relaxed mb-3">{p}</p>
          ))}
        </div>
      )}
    </motion.div>
  );
}

/* ── Speaking View ────────────────────────────────────────────── */
const SPEAKING_PARTS = [
  {
    part: 1,
    title: "Part 1 — Introduction & Interview",
    duration: "4-5 минут",
    questions: [
      { q: "Can you tell me your full name?", sample: "My full name is [Your Name]. I'm originally from [City], Mongolia." },
      { q: "Where are you from?", sample: "I'm from Ulaanbaatar, the capital city of Mongolia. It's a vibrant city with a mix of modern and traditional culture." },
      { q: "Do you work or are you a student?", sample: "I'm currently a student at [University Name], studying [Subject]. I'm in my third year." },
      { q: "What do you enjoy doing in your free time?", sample: "In my free time, I enjoy reading books and going hiking. Mongolia has beautiful landscapes, so I try to spend time outdoors whenever I can." },
      { q: "Do you like your hometown? Why?", sample: "Yes, I do. Ulaanbaatar has changed a lot in recent years and has become quite cosmopolitan. However, I sometimes miss the quieter pace of life in the countryside." },
    ],
  },
  {
    part: 2,
    title: "Part 2 — Individual Long Turn",
    duration: "3-4 минут",
    questions: [
      {
        q: "Describe a person who has had a significant influence on your life.\n\nYou should say:\n• Who this person is\n• How you know them\n• What they have done\n• And explain why they have had such an influence on you.",
        sample: "I'd like to talk about my secondary school teacher, Mr. Dorj, who taught mathematics. I met him when I was about fourteen years old, and he had a profound impact on my academic development.\n\nMr. Dorj was exceptional because he had a gift for making complex mathematical concepts accessible and even enjoyable. He never made students feel embarrassed about making mistakes; instead, he encouraged us to view errors as learning opportunities.\n\nWhat made him particularly influential was his belief in my potential when I doubted myself. During a particularly difficult period when I was struggling with advanced algebra, he spent extra time with me after school, patiently explaining concepts until I understood them.\n\nHis influence extended beyond mathematics. He taught me the value of persistence and the importance of approaching problems systematically. These skills have proved invaluable in my university studies and in life generally. I believe that good teachers can genuinely change the direction of a student's life, and Mr. Dorj certainly changed mine.",
      },
    ],
  },
  {
    part: 3,
    title: "Part 3 — Two-way Discussion",
    duration: "4-5 минут",
    questions: [
      { q: "How important is education in today's society?", sample: "Education is fundamentally important in contemporary society for several reasons. Firstly, it equips individuals with the knowledge and skills needed to participate in an increasingly complex economy. Secondly, education promotes critical thinking and informed citizenship, which are essential for functioning democracies. However, I think we also need to recognise that education extends beyond formal schooling — lifelong learning has become essential given how rapidly technology and industries are changing." },
      { q: "Do you think technology has changed the way people learn?", sample: "Absolutely, technology has transformed education in profound ways. Online learning platforms have democratised access to knowledge, allowing people in remote areas to access world-class educational content. However, I think there are also concerns — the abundance of information online can be overwhelming, and not all of it is reliable. The challenge for modern educators is to teach students how to evaluate sources critically rather than simply accept information at face value." },
      { q: "What are the advantages and disadvantages of studying abroad?", sample: "Studying abroad offers tremendous benefits, particularly in terms of personal development and cultural exposure. Students gain independence, develop cross-cultural communication skills, and often learn a new language. These experiences are highly valued by employers. On the other hand, studying abroad can be financially challenging and emotionally difficult, particularly for students who are far from their families for the first time. There's also the risk that students may struggle to reintegrate into their home country's job market after spending years abroad." },
    ],
  },
];

function SpeakingView({ onBack }: { onBack: () => void }) {
  const [activePart, setActivePart] = useState(0);
  const [expandedQ, setExpandedQ] = useState<number | null>(null);
  const part = SPEAKING_PARTS[activePart];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20">
      <div className="flex items-center gap-4 mb-8">
        <Mic className="w-8 h-8 text-[#8B5CF6]" />
        <div>
          <h2 className="text-2xl font-black text-white">IELTS Speaking</h2>
          <p className="text-white/50 text-sm">Part 1, 2, 3 — жишиг хариулттай</p>
        </div>
        <button onClick={onBack} className="ml-auto text-white/40 hover:text-white text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Буцах
        </button>
      </div>

      {/* Part tabs */}
      <div className="flex gap-2 mb-8">
        {SPEAKING_PARTS.map((p, i) => (
          <button key={p.part} onClick={() => { setActivePart(i); setExpandedQ(null); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${activePart === i ? "border-[#8B5CF6] bg-[#8B5CF6]/20 text-white" : "border-white/10 bg-white/5 text-white/50 hover:text-white"}`}>
            Part {p.part}
          </button>
        ))}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold">{part.title}</h3>
          <span className="text-white/40 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />{part.duration}</span>
        </div>
        <p className="text-white/40 text-xs mt-1">Асуулт дарж жишиг хариулт харна уу</p>
      </div>

      <div className="space-y-4">
        {part.questions.map((item, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <button onClick={() => setExpandedQ(expandedQ === i ? null : i)}
              className="w-full flex items-start gap-4 p-5 text-left hover:bg-white/5 transition-colors">
              <span className="w-7 h-7 rounded-full bg-[#8B5CF6]/30 text-[#8B5CF6] text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
              <div className="flex-1">
                <p className="text-white text-sm leading-relaxed whitespace-pre-line">{item.q}</p>
              </div>
              {expandedQ === i ? <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0 mt-1" />}
            </button>
            {expandedQ === i && (
              <div className="px-5 pb-5 border-t border-white/10">
                <div className="text-[#8B5CF6] font-bold text-xs mb-3 mt-4">✓ Жишиг хариулт (Band 7-8)</div>
                <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line">{item.sample}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ── Progress View ────────────────────────────────────────────── */
function ProgressView({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { data } = trpc.ielts.getProgress.useQuery({ sessionId });
  if (!data) return null;

  const sections = ["listening", "reading"] as const;
  const sectionNames: Record<string, string> = { listening: "Listening", reading: "Reading" };
  const sectionIcons: Record<string, React.ReactNode> = {
    listening: <Headphones className="w-5 h-5" />,
    reading: <BookOpen className="w-5 h-5" />,
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="container py-32 pb-20 max-w-3xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <BarChart3 className="w-8 h-8 text-[#00A86B]" />
        <h2 className="text-2xl font-black text-white">Миний Явц</h2>
        <button onClick={onBack} className="ml-auto text-white/40 hover:text-white text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Буцах
        </button>
      </div>

      {/* Placement */}
      {data.placement && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <div className="text-white/50 text-sm mb-3">Түвшин тогтоох шалгалт</div>
          <div className="flex items-center gap-4">
            <LevelBadge level={data.placement.level} />
            <span className="text-2xl font-black" style={{ color: bandColor(data.placement.bandScore) }}>Band {data.placement.bandScore}</span>
            <span className="text-white/40 text-sm">{data.placement.score}/20 зөв</span>
          </div>
        </div>
      )}

      {/* Section results */}
      {sections.map((sec) => {
        const attempts = data.attempts.filter((a) => a.section === sec);
        if (attempts.length === 0) return null;
        return (
          <div key={sec} className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <span style={{ color: sec === "listening" ? "#1E6FD9" : "#00A86B" }}>{sectionIcons[sec]}</span>
              <span className="text-white font-bold">{sectionNames[sec]}</span>
            </div>
            <div className="space-y-3">
              {attempts.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                  <div>
                    <span className="text-white/50 text-xs">Section {a.sectionNumber}</span>
                    <div className="text-white text-sm font-medium">{a.score}/{a.totalQuestions} зөв</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-black" style={{ color: bandColor(a.bandScore) }}>Band {a.bandScore}</div>
                    <div className="text-white/30 text-xs">{new Date(a.createdAt).toLocaleDateString("mn-MN")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {data.attempts.length === 0 && !data.placement && (
        <div className="text-center py-16 text-white/30">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>Одоогоор шалгалт өгөөгүй байна.</p>
        </div>
      )}
    </motion.div>
  );
}
